import {
  APPWRITE_DATABASE_ID,
  PROJECT_DEVICES_TABLE_ID,
  tablesDB
} from "src/handlers/Appwrite";

import { parseJson } from "./internal";

// ---------------------------------------------------------------------------
// Conflict detection for device rows.
//
// `syncSection` may be called with `expectedAtMs`: the timestamp of the
// device tab we last saw locally. If the server row has been updated since
// then, somebody else wrote in between and we should merge instead of
// blindly overwriting. We probe the server first; the caller decides what
// to do with the returned `serverTab`.
// ---------------------------------------------------------------------------

/**
 * @param {string} rowId        Deterministic device row id.
 * @param {number} expectedAtMs `updatedAtMs` we last observed for the row.
 * @returns {Promise<{conflict: false} | {conflict: true, serverTab: any, serverTs: number}>}
 */
export async function checkDeviceServerVersion(rowId, expectedAtMs) {
  if (!Number.isFinite(expectedAtMs) || expectedAtMs <= 0) {
    return { conflict: false };
  }

  try {
    const serverRow = await tablesDB.getRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_DEVICES_TABLE_ID,
      rowId
    });
    const serverTs = Number(serverRow?.updatedAtMs || 0);
    if (serverTs > expectedAtMs) {
      return {
        conflict: true,
        serverTab: parseJson(serverRow.dataJson, null),
        serverTs
      };
    }
    return { conflict: false };
  } catch (error) {
    // 404 = row doesn't exist yet, no conflict possible.
    if (error?.code === 404) return { conflict: false };
    throw error;
  }
}

/**
 * Take the server's tab and overwrite a single section with the value the
 * caller wanted to push. Used to resolve `syncSection` conflicts: the
 * remote edits to other sections are preserved, only the local section
 * change wins.
 */
export function mergeServerSection(serverTab, sectionKey, sectionValue) {
  return { ...(serverTab || {}), [sectionKey]: sectionValue };
}
